<template>
  <div>
    <CommentInner
      class="border-b border-black/15 pb-4 last-of-type:border-none"
      :comment="comment"
      :id="id"
      :liked-by-me="likedByMe"
      :name="name"
      :like-count="likeCount"
      :reply-count="replyCount"
      :content-id="id"
      :avatar-url="avatarUrl"
      :is-sending="isSending"
      :is-replies-fetching="repliesStates[id as string]?.isFetching"
      :is-reply-form-open="repliesStates[id as string]?.isOpen"
      :is-report-form-open="reportStates[id as string]?.isOpen"
      @set-reply-form="setReplyForm(id, name, comment, replyCount, id)"
      @set-report-form="setReportForm(id)"
      @clear-replies="clearReplyForm(id as string)"
    />
    <transition name="fade" mode="out-in">
      <div
        v-if="replies && repliesStates[id as string]?.isOpen"
        class="grid gap-y-4 border-t border-black/10 py-4 pl-4 last-of-type:border-none"
      >
        <div v-for="comment in replies" :key="comment.id">
          <CommentInner
            class="border-b border-black/10 pb-4"
            :comment="comment.comment"
            :id="comment.id"
            :liked-by-me="comment.likedByMe"
            :name="comment.name"
            :like-count="comment.likeCount"
            :reply-count="comment.replyCount"
            is-replied-comment
            :content-id="id"
            :avatar-url="comment.avatarUrl"
            :is-sending="isSending"
            :is-replies-fetching="repliesStates[comment.id as string]?.isFetching"
            :is-report-form-open="reportStates[id as string]?.isOpen"
            @set-report-form="setReportForm(id)"
            @set-reply-form="
              setReplyForm(comment.id, comment.name, comment.comment, comment.replyCount, id)
            "
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useCommentStore } from '@stores/comment.store';

// Emits
const emit = defineEmits(['clickCallback']);

// Interfaces
import type { CommentInterface } from './comment.interface';

// Components
import CommentInner from '@modules/Comment/CommentInner.vue';

// Props
const props = defineProps<CommentInterface>();

// Refs
const replies = ref<CommentInterface[] | null>(null);
const isSending = ref(false);
const repliesStates = ref<Record<string, { isFetching: boolean; isOpen: boolean }>>({});
const reportStates = ref<Record<string, { isOpen: boolean }>>({});

// Stores
const commentStore = useCommentStore();

// Watchers
watch(
  () => props.comments,
  async (value) => {
    await fetchReplies();

    if (repliesStates.value[props.id as string])
      repliesStates.value[props.id as string].isOpen = true;

    replies.value = value ?? [];
  },
);

// Methods
const showRepliedComments = (id: string) => {
  if (!repliesStates.value[id]) {
    repliesStates.value[id] = { isFetching: false, isOpen: false };
  }
  repliesStates.value[id].isOpen = true;
};

const clearReplyForm = async (id: string) => {
  await commentStore.clearReplyData();

  if (repliesStates.value[id]) {
    repliesStates.value[id].isOpen = false;
  }
  if (id === props.id) {
    replies.value = null;
  }

  emit('clickCallback');
};

const setReplyForm = async (
  replyToId: CommentInterface['replyToId'],
  name: CommentInterface['name'],
  comment: CommentInterface['comment'],
  replyCount: CommentInterface['replyCount'],
  parentId: CommentInterface['id'],
) => {
  const replyId = String(replyToId);

  // Ensure state object exists for this reply
  if (!repliesStates.value[replyId]) {
    repliesStates.value[replyId] = { isFetching: false, isOpen: false };
  }

  // Show replied comments for the main comment
  showRepliedComments(props.id as string);

  // Store reply data in the comment store
  commentStore.storeReplyData(String(parentId) || replyId, String(name), String(comment));
  commentStore.setReportCommentForm(false);

  // Fetch replies if needed, otherwise close the reply form
  if (replyCount && replyCount > 0 && !replies.value) {
    repliesStates.value[replyId].isFetching = true;
    await fetchReplies();
    repliesStates.value[replyId].isFetching = false;
  } else {
    repliesStates.value[replyId].isOpen = false;
  }

  emit('clickCallback');
};

const setReportForm = (id?: string) => {
  if (!id) return;

  if (!reportStates.value[id]) {
    reportStates.value[id] = { isOpen: false };
  }
  reportStates.value[id].isOpen = !reportStates.value[id].isOpen;
};

const fetchReplies = async () => {
  await commentStore.fetchComments(props.contentId, props.id);
  replies.value = commentStore.commentData;
};
</script>
